/*
 * Implement ExportOptions for Tableau as we only need to support PNG and `filename` for now.
 */
import React, {useState} from 'react';
import {
  AnchorButton,
  FormGroup,
  Icon,
  InputGroup,
  Intent,
  Position,
} from '@blueprintjs/core';
import {IconNames} from '@blueprintjs/icons';
import {Popover2} from '@blueprintjs/popover2';

import {IBlock} from '../../../models';
import {
  PNGExport,
  getExportOptionsFromBlock,
} from '../components';
import {ACTION_TYPES} from './constants';
import {ConvertColumnDatatype} from './ConvertColumnDatatype';

import '../components/ExportOptions/ExportOptions.scss';


type OnChangeCallback = (key: string) => (value: any) => void;

interface ExportOptions {
  embed: boolean;
  delimiter: string; // CSV delimiter
  quote_char: string; // CSV quote char (which character should be escaped)
  filename: string;
  exportOptions: any;
  column_properties: any;
  datatypes_parsing: boolean;
  workbook_locale: string;
}

interface ExportOptionsProps {
  show: boolean;
  block: IBlock | null;
  action: string;
  onChange: OnChangeCallback;
  disabled?: boolean;
  uid: string;
  integrationId: string;
  viewId: string;
}

interface ExportOptionProps {
  embed: boolean;
  embedDisabled?: boolean;
  filename: string;
  delimiter: string;
  quoteChar: string;
  datatypeParsing: boolean;
  workbookLocale: string;
  columnProperties: any;
  isCSV: boolean;
  isXLSX: boolean;
  onChange: OnChangeCallback;
  disabled: boolean;
  action: string;
  uid: string;
  integrationId: string;
  viewId: string;
}

const ExportOption = ({
  embed = false,
  embedDisabled = false,
  filename = '',
  delimiter = '',
  quoteChar = '',
  datatypeParsing = false,
  workbookLocale = '',
  columnProperties,
  isCSV,
  isXLSX,
  onChange,
  disabled,
  action,
  uid,
  integrationId,
  viewId,
}: ExportOptionProps) => {
  const [quoteCharError, setQuoteCharError] = useState(false);

  const onDelimiterChange = (event: any) => {
    onChange('delimiter')(event.target.value);
  }

  const onDataTypesChange = (value: any) => {
    onChange('column_properties')(value);
  }

  const onDataTypeParsingChange = (event: any) => {
    onChange('datatypes_parsing')(event.target.checked);
  }

  const onWorkbookLocaleChange = (event: any) => {
    onChange('workbook_locale')(event.value);
  }

  const onQuoteCharChange = (event: any) => {
    const value = event.target.value;

    if (value.length > 1) {
      /*
       * Quote character can be of length 1 only
       */
      setQuoteCharError(true);
      return;
    } else if (quoteCharError) {
      setQuoteCharError(false);
    }
    onChange('quote_char')(event.target.value);
  }

  return (
    <div className="export-options-popover">
      <div className="title">Export Options</div>
      <div className="selected-option-content">
        <PNGExport
          embed={embed}
          embedDisabled={embedDisabled}
          filename={filename}
          onChange={onChange}/>
        {
          isCSV && (
            <>
              <FormGroup inline={true} label="CSV Delimiter">
                <InputGroup
                  defaultValue={delimiter}
                  autoComplete="off"
                  onChange={onDelimiterChange}
                  placeholder=","
                />
              </FormGroup>
              <FormGroup
                inline={true}
                label="CSV Quote Char"
                helperText={quoteCharError ? "Quote Char is 1 character only." : undefined}
              >
                <InputGroup
                  defaultValue={quoteChar}
                  autoComplete="off"
                  onChange={onQuoteCharChange}
                  placeholder='"'
                  intent={quoteCharError ? Intent.DANGER : Intent.NONE}
                />
              </FormGroup>
              <ConvertColumnDatatype
                columnProperties={columnProperties}
                onDataTypesChange={onDataTypesChange}
                onDataTypeParsingChange={onDataTypeParsingChange}
                onWorkbookLocaleChange={onWorkbookLocaleChange}
                disabled={disabled}
                action={action}
                uid={uid}
                integrationId={integrationId}
                viewId={viewId}
                datatypeParsing={datatypeParsing}
                workbookLocale={workbookLocale}
                isCSV={isCSV}
              />
            </>
          )
        }
        {
          isXLSX && (
            <ConvertColumnDatatype
              columnProperties={columnProperties}
              onDataTypesChange={onDataTypesChange}
              onDataTypeParsingChange={onDataTypeParsingChange}
              onWorkbookLocaleChange={onWorkbookLocaleChange}
              disabled={disabled}
              action={action}
              uid={uid}
              integrationId={integrationId}
              viewId={viewId}
              datatypeParsing={datatypeParsing}
              workbookLocale={workbookLocale}
              isCSV={isCSV}
            />
          )
        }
      </div>
    </div>
  )
};

export const ExportOptions = React.memo(({
  show,
  block,
  action,
  onChange,
  disabled = false,
  uid,
  integrationId,
  viewId,
}: ExportOptionsProps) => {
  if (!show) {
    return null;
  }
  const exportOptions: ExportOptions | null = getExportOptionsFromBlock(block as IBlock);
  const name = exportOptions?.filename || block?.name as string;
  const isCSV = action === ACTION_TYPES.VIEW_CSV;
  const isXLSX = action === ACTION_TYPES.VIEW_EXCEL;

  return (
    <div className="export-options">
      <Icon icon={IconNames.NEST}/>
      <Popover2
        content={
          <ExportOption
            embed={exportOptions?.embed as boolean}
            filename={exportOptions?.filename as string}
            delimiter={exportOptions?.delimiter as string}
            quoteChar={exportOptions?.quote_char as string}
            embedDisabled={action !== ACTION_TYPES.VIEW_IMAGE}
            datatypeParsing={exportOptions?.datatypes_parsing as boolean}
            workbookLocale={exportOptions?.workbook_locale as string}
            columnProperties={exportOptions?.column_properties as any}
            isCSV={isCSV}
            isXLSX={isXLSX}
            onChange={onChange}
            disabled={disabled}
            action={action}
            uid={uid}
            integrationId={integrationId}
            viewId={viewId}
          />
        }
        position={Position.BOTTOM_RIGHT}
      >
        <AnchorButton
          small={true}
          text={name}
          icon={IconNames.PAPERCLIP}
          rightIcon={IconNames.COG}
          disabled={disabled}
        />
      </Popover2>
    </div>
  );
});
